import React, { useEffect, useMemo } from 'react';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useAppForm from '@app/site/_form';
import * as z from 'zod/v4';
import PricingStrategyCompetitorImportance from '@app/model/pricing-automation/PricingStrategyCompetitorImportance';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import Message from '@ui/message/Message';
import FormButton from '@ui/button/FormButton';
import Portlet from '@ui/portlet/Portlet';
import ProgressBar from '@ui/progress/ProgressBar';
import InfoTooltip from '@ui/tooltip/InfoTooltip';
import ScaleButtons from '@ui/scale/ScaleButtons';
import _isEmpty from 'lodash/isEmpty';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import PricingStrategyCompetitorsImportanceInput
	from '@app/model/pricing-automation/input/PricingStrategyCompetitorsImportanceInput';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import {
	allImportancesZero,
	competitorImportanceDefaultsMutationKey,
	findMissingCompetitorImportances,
	formatImportanceShare,
	IMPORTANCE_WORDS,
	importanceShare,
	normalizeCompetitorImportances,
	pricingHotelCompetitors,
} from '@app/site/secured/rateshopper/pricing-automation/competitor-importance/PricingAutomationStrategyCompetitorsImportance.utilities';


const PricingAutomationStrategyCompetitorsImportancePage = React.memo(() => {
	const { companyHotel } = useSelectedCompanyHotel();
	const hotelCompetitors = useMemo(() => pricingHotelCompetitors(companyHotel.competitors), [companyHotel.competitors]);

	const { data, isLoading, isSuccess } = useQuery({
		queryFn: () => PricingAutomationService.findCompanyHotelPricingStrategyCompetitorImportances(companyHotel.id),
		queryKey: PricingAutomationQueryKeys.pricingStrategyCompetitorImportances(companyHotel.id),
	});

	const queryKey = PricingAutomationQueryKeys.pricingStrategyCompetitorImportances(companyHotel.id);

	const queryClient = useQueryClient();
	const missingCompetitorImportances = useMemo(
		() => findMissingCompetitorImportances(hotelCompetitors, data),
		[hotelCompetitors, data],
	);
	const normalizedCompetitorImportances = useMemo(
		() => normalizeCompetitorImportances(hotelCompetitors, data),
		[hotelCompetitors, data],
	);

	const createDefaultCompetitorImportancesMutation = useMutation({
		mutationKey: competitorImportanceDefaultsMutationKey(companyHotel.id),
		mutationFn: PricingAutomationService.savePricingStrategyCompetitorImportance,
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey });
		},
	});

	useEffect(() => {
		if (!isSuccess || _isEmpty(missingCompetitorImportances) || !createDefaultCompetitorImportancesMutation.isIdle) {
			return;
		}

		const input: PricingStrategyCompetitorsImportanceInput = {
			companyHotelId: companyHotel.id,
			competitorImportances: missingCompetitorImportances,
		};

		createDefaultCompetitorImportancesMutation.mutate({ input });
	}, [
		companyHotel.competitors,
		companyHotel.id,
		createDefaultCompetitorImportancesMutation,
		isSuccess,
		missingCompetitorImportances,
	]);

	if (isLoading || createDefaultCompetitorImportancesMutation.isPending) return <TablePlaceholder />;
	if (isSuccess) {
		if (createDefaultCompetitorImportancesMutation.isError) {
			return <Message state="error">
				<span>Failed to create default competitor importances.</span>
				<FormButton state="text" isLink className="font-medium"
					onClick={() => createDefaultCompetitorImportancesMutation.reset()}>
					Retry
				</FormButton>
			</Message>;
		}

		return <PricingAutomationStrategyCompetitorsImportances
			companyHotel={companyHotel}
			competitorImportances={normalizedCompetitorImportances}
		/>;
	}

	return <Message state="error">There is no competitor importances defined!. Please contact support!</Message>;


});

const PricingAutomationStrategyCompetitorsImportances: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	competitorImportances: PricingStrategyCompetitorImportance[]
}>
	= ({ companyHotel, competitorImportances }) => {

	const toaster = useToaster();
	const queryKey = PricingAutomationQueryKeys.pricingStrategyCompetitorImportances(companyHotel.id);
	const queryClient = useQueryClient();

	const saveCompetitorImportancesMutation = useMutation({
		mutationFn: PricingAutomationService.savePricingStrategyCompetitorImportance,
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey })
				.then(() => toaster.success('Pricing Automation', 'Competitor importances saved successfully!'));
		},
		onError: () => {
			toaster.error('Pricing Automation', 'Failed to save competitor importances. Please try again.');
		},
	});

	// All competitors at 0 leave the recommendations with no competitor price: the form does not save that.
	const PricingStrategyCompetitorsImportanceSchema = z.object({
		competitorsImportance: z.array(z.object({
			competitorId: z.number(),
			importance: z.number().min(0).max(5),
		})).refine(list => list.length === 0 || !allImportancesZero(list.map(ci => ci.importance))),
	});

	const form = useAppForm({
		defaultValues: {
			competitorsImportance: competitorImportances.map(ci => ({
				competitorId: ci.competitorId,
				importance: Number(ci.importance),
			})),
		},
		validators: {
			onChange: PricingStrategyCompetitorsImportanceSchema,
		},
		onSubmit: ({ value }) => {
			saveCompetitorImportancesMutation.mutate({
				input: {
					companyHotelId: companyHotel.id,
					competitorImportances: value.competitorsImportance.map((ci) => ({
						competitorId: ci.competitorId,
						importance: String(ci.importance),
					})),
				},
			});
		},
	});

	return <form onSubmit={(e) => {
		e.preventDefault();
		e.stopPropagation();
	}}>
		<form.AppForm>
			<form.Subscribe selector={state => state.values.competitorsImportance.map(ci => ci.importance)}>
				{importances => {
					const isAllZero = importances.length > 0 && allImportancesZero(importances);
					return <>
						<Portlet title="Competitors" isLarge>
							<p className="max-w-3xl text-sm text-gray-600">
								Importance sets how much each competitor’s price counts in the market price of your price
								recommendations. A competitor at&nbsp;0 is not used. A&nbsp;5 counts five times as much
								as a&nbsp;1.
							</p>

							{isAllZero && <Message state="warning" extraClassName="mt-4">
								<span>
									All competitors are at 0, so your recommendations use no competitor prices. They use
									only your own past prices as the market. Give at least one competitor an importance of
									1 or more.
								</span>
							</Message>}

							{importances.length === 0
								? <p className="mt-5 text-sm text-gray-500">This hotel has no hotel competitors.</p>
								: <div role="table" aria-label="Competitor importance" className="mt-5">
									<div role="row"
										className="hidden border-b border-gray-300 pb-2.5 text-sm font-semibold text-gray-900 lg:flex lg:items-end lg:gap-x-6">
										<div role="columnheader" className="min-w-0 flex-1">Competitor</div>
										<div role="columnheader" className="w-96 flex-none">Importance</div>
										<div role="columnheader" className="flex w-56 flex-none items-center gap-x-1.5">
											Share
											<InfoTooltip label="About the share" title="Share">
												The part of each competitor in the market price when all competitors have a price
												for the night.
											</InfoTooltip>
										</div>
									</div>
									{competitorImportances.map((c, index) => {
										const name = companyHotel.findCompetitorById(c.competitorId)?.name ?? '';
										const importance = importances[index];
										const share = importanceShare(importance, importances);
										const isUnused = importance === 0;
										return <div key={c.competitorId} role="row"
											className="flex flex-col gap-y-2.5 border-b border-gray-200 py-3.5 lg:flex-row lg:items-center lg:gap-x-6 lg:py-2.5">
											<div role="cell"
												className={`min-w-0 flex-1 truncate text-sm font-medium ${isUnused ? 'text-gray-400' : 'text-gray-700'}`}>
												{name}
											</div>
											<div role="cell" className="flex flex-none items-center gap-x-3 lg:w-96">
												<form.AppField name={`competitorsImportance[${index}].importance`}>
													{field => <ScaleButtons label={`Importance of ${name}`}
														value={field.state.value} onChange={field.handleChange}
														offValue={0} optionLabels={IMPORTANCE_WORDS} />}
												</form.AppField>
												<span className={`w-20 whitespace-nowrap text-sm ${isUnused ? 'text-gray-400' : 'text-gray-600'}`}>
													{IMPORTANCE_WORDS[importance]}
												</span>
											</div>
											<div role="cell" className="flex flex-none items-center gap-x-3 lg:w-56">
												<span className="flex w-14 flex-none items-center gap-x-1 text-xs text-gray-500 lg:hidden">
													Share
													<InfoTooltip label={`About the share of ${name}`} title="Share">
														The part of each competitor in the market price when all competitors have a
														price for the night.
													</InfoTooltip>
												</span>
												<div className="min-w-0 flex-1">
													<ProgressBar label={`Share of ${name}`} value={share ?? 0} max={100} />
												</div>
												<span
													className={`w-14 flex-none text-right text-sm tabular-nums ${isUnused ? 'text-gray-400' : 'text-gray-700'}`}>
													{formatImportanceShare(share)}
												</span>
											</div>
										</div>;
									})}
								</div>}
						</Portlet>

						{/* Actions */}
						<div className="mt-4 flex justify-end gap-x-3 py-4">
							<form.ResetButton state="white">Reset</form.ResetButton>
							<form.SubmitButton state="primary" disabled={isAllZero}>Save</form.SubmitButton>
						</div>
					</>;
				}}
			</form.Subscribe>
		</form.AppForm>
	</form>;
};


export default PricingAutomationStrategyCompetitorsImportancePage;
